import React from 'react';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { SUMMARY_CARDS } from '../../../constants';
import { getCountryLabel, getCountryRank } from '../../../lib';
import { ActivityCountryMetrics, ActivitySummaryMetrics } from '../../../types';
import { getCountryFlag } from './country';

interface Props {
  summary: ActivitySummaryMetrics | null;
  loading?: boolean;
}

const sortByCountry = (countries: ActivityCountryMetrics[]): ActivityCountryMetrics[] =>
  [...countries].sort((a, b) => getCountryRank(a.country) - getCountryRank(b.country));

export const SummaryCards = ({ summary, loading }: Props) => {
  const byCountry = summary ? sortByCountry(summary.countries) : [];

  return (
    <CardContainer title="Summary">
      <Box
        sx={{
          mt: 1,
          gap: '1rem',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
        }}
      >
        {SUMMARY_CARDS.map(({ title, getValue, format, valueColor }) => (
          <Card key={title} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Typography variant="body1" fontWeight={500}>
                {title}:
              </Typography>

              <Typography variant="h6" sx={{ color: valueColor, whiteSpace: 'nowrap' }}>
                {loading ? <Skeleton width={70} /> : summary ? format(getValue(summary)) : '—'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={2} rowGap={0.5} sx={{ mt: 1 }}>
              {loading ? (
                <Skeleton width="60%" />
              ) : (
                byCountry.map(country => {
                  const flag = getCountryFlag(country.country);

                  return (
                    <Stack key={country.country} direction="row" alignItems="center" gap={0.5}>
                      {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                      <Typography variant="body2" color="text.secondary">
                        {getCountryLabel(country.country)}:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {format(getValue(country))}
                      </Typography>
                    </Stack>
                  );
                })
              )}
            </Stack>
          </Card>
        ))}
      </Box>
    </CardContainer>
  );
};
